/**
 * BONE WATER — FLOAT BLEND SWAP
 * assets/bw-float-swap.js
 *
 * Flies the decorative cut-outs off and on when the blend changes: the piece
 * leaving heads for its nearest viewport edge, the piece arriving does the
 * reverse and comes in from that same edge. Driven off the `bw:blendchange`
 * event the header and hero switchers already dispatch.
 *
 * Section-agnostic on purpose. Any section that renders `.bw-float` elements
 * carrying `data-b="<blend key>"` is picked up with no work on its side —
 * currently bw-blend-hero and bw-how-to-use. Floats without `data-b` (the
 * bw-hero-split ones, which are shared across blends) are left alone, since
 * nothing about them changes when the blend does.
 *
 * Presentation lives in bw-tokens.css: .is-swap-out, .is-swap-in, .is-swap-arm
 * and the --bw-*-swap-* tokens. This file only decides direction and distance.
 *
 * Requires: assets/bw-tokens.css
 */
(function () {
  /* Loaded by every float-bearing section, so it can arrive more than once on
     a page. Guard rather than fight it — a second set of listeners would run
     the swap twice and cancel its own cleanup timers. */
  if (window.__bwFloatSwapBound) return;
  window.__bwFloatSwapBound = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Clearance past the edge, so nothing is left peeking on a rounded display
     or under a browser chrome inset. */
  var MARGIN = 24;

  function ms(name, fallback) {
    var v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (!v) return fallback;
    return v.slice(-2) === 'ms' ? parseFloat(v) : parseFloat(v) * 1000;
  }

  /**
   * Nearest viewport edge, and the translation that carries the element fully
   * past it. Distances are measured edge-to-edge, so a cut-out already half
   * off the left of the screen correctly picks left rather than the edge its
   * centre happens to sit nearer.
   */
  function flight(rect) {
    var vw = window.innerWidth;
    var vh = window.innerHeight;

    var options = [
      { gap: rect.left,       x: -(rect.right + MARGIN),   y: 0 },
      { gap: vw - rect.right, x: (vw - rect.left) + MARGIN, y: 0 },
      { gap: rect.top,        x: 0, y: -(rect.bottom + MARGIN) },
      { gap: vh - rect.bottom, x: 0, y: (vh - rect.top) + MARGIN }
    ];

    var best = options[0];
    for (var i = 1; i < options.length; i++) {
      if (options[i].gap < best.gap) best = options[i];
    }
    return best;
  }

  /* A section well below the fold has floats whose "nearest viewport edge" is
     hundreds of pixels away and off screen anyway. Swapping them plainly is
     both cheaper and more correct than flinging them a distance nobody sees. */
  function onScreen(rect) {
    return rect.bottom > 0 && rect.top < window.innerHeight &&
           rect.right > 0 && rect.left < window.innerWidth;
  }

  function clear(el) {
    clearTimeout(el._bwSwapTimer);
    el.classList.remove('is-swap-out', 'is-swap-in', 'is-swap-arm', 'is-intro-pending');
    el.style.transform = '';
    /* Releases any cut-out pinned by a paired flight below. */
    el.querySelectorAll('img[data-b]').forEach(function (img) { img.style.display = ''; });
  }

  /**
   * bw-hero-split composes a per-blend float differently to bw-blend-hero: one
   * container holding a move <img> and a restore <img>, with the section's own
   * CSS display:none-ing whichever is inactive. The container carries no
   * data-b, so the selector in swap() skips it and those cut-outs simply cut
   * where the blend hero's fly. Collected here so they fly too.
   */
  function paired(from, to) {
    var list = [];
    document.querySelectorAll('.bw-float:not([data-b])').forEach(function (el) {
      var leaving = el.querySelector('img[data-b="' + from + '"]');
      var arriving = el.querySelector('img[data-b="' + to + '"]');
      if (leaving && arriving) list.push({ el: el, out: leaving, into: arriving });
    });
    return list;
  }

  function swap(from, to) {
    var out = [];
    var into = [];

    document.querySelectorAll('.bw-float[data-b]').forEach(function (el) {
      var b = el.getAttribute('data-b');
      if (b === from) out.push(el);
      else if (b === to) into.push(el);
    });

    var pairs = paired(from, to);

    if (!out.length && !into.length && !pairs.length) return;

    var all = out.concat(into);

    /* Three passes, deliberately not interleaved. Resetting anything still in
       flight from a previous toggle invalidates layout, so every reset has to
       land before the first measurement or the reads thrash layout once per
       float — and worse, later floats would be measured after earlier writes
       had already moved them. */
    all.forEach(clear);
    pairs.forEach(function (p) { clear(p.el); });

    var outMs = ms('--bw-dur-swap-out', 240);
    var inMs = ms('--bw-dur-swap-in', 260) + ms('--bw-delay-swap-in', 70);

    /* Paired floats first: same flight, but one element making a round trip
       rather than two swapping places. */
    pairs.forEach(function (p) {
      var rect = p.el.getBoundingClientRect();
      if (!rect.width || !rect.height || !onScreen(rect)) return;

      var f = flight(rect);
      var t = 'translate3d(' + f.x.toFixed(1) + 'px, ' + f.y.toFixed(1) + 'px, 0)';

      /* The section hides the inactive image with a plain display:none, so an
         inline display outranks it and holds the outgoing cut-out up for the
         whole flight. Without this the new image is already on screen as the
         piece leaves, and the swap reads as a glitch rather than a change. */
      p.out.style.display = 'block';
      p.into.style.display = 'none';

      p.el.classList.add('is-swap-out');
      p.el.style.transform = t;

      p.el._bwSwapTimer = setTimeout(function () {
        /* Off screen now. Hand the images back to CSS — which shows the new
           blend's cut-out — then park and fly the same element back in. */
        p.out.style.display = '';
        p.into.style.display = '';

        p.el.classList.remove('is-swap-out');
        p.el.classList.add('is-swap-arm');
        p.el.style.transform = t;

        void document.documentElement.offsetWidth;

        p.el.classList.remove('is-swap-arm');
        p.el.classList.add('is-swap-in');
        p.el.style.transform = '';
        p.el._bwSwapTimer = setTimeout(function () { clear(p.el); }, inMs + 60);
      }, outMs);
    });

    var plan = [];
    all.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height || !onScreen(rect)) return;
      plan.push({ el: el, to: el.getAttribute('data-b') === to, flight: flight(rect) });
    });
    if (!plan.length) return;

    plan.forEach(function (p) {
      var t = 'translate3d(' + p.flight.x.toFixed(1) + 'px, ' + p.flight.y.toFixed(1) + 'px, 0)';

      if (!p.to) {
        p.el.classList.add('is-swap-out');
        p.el.style.transform = t;
        p.el._bwSwapTimer = setTimeout(function () { clear(p.el); }, outMs + 60);
        return;
      }

      /* Park it off its edge with motion suppressed... */
      p.el.classList.add('is-swap-arm');
      p.el.style.transform = t;
    });

    var arrivals = plan.filter(function (p) { return p.to; });
    if (!arrivals.length) return;

    /* ...force the parked position to be recorded as the transition's start
       value, then release. Reading offsetWidth once covers every arrival —
       style is flushed document-wide. */
    void document.documentElement.offsetWidth;

    arrivals.forEach(function (p) {
      p.el.classList.remove('is-swap-arm');
      p.el.classList.add('is-swap-in');
      p.el.style.transform = '';
      p.el._bwSwapTimer = setTimeout(function () { clear(p.el); }, inMs + 60);
    });
  }

  function current() {
    return document.documentElement.getAttribute('data-bw-blend');
  }

  /* The header's inline script sets the attribute during parse and does not
     dispatch, so the first value is read rather than waited for. */
  var previous = current();

  document.addEventListener('bw:blendchange', function (e) {
    var to = (e.detail && e.detail.blend) || current();
    var from = previous;
    previous = to;

    if (!from || from === to || reduce.matches) return;
    swap(from, to);
  });

  /* The editor re-renders a section mid-flight; anything left mid-transition
     would keep a stale inline transform once its timer died with the old node. */
  document.addEventListener('shopify:section:load', function () {
    previous = current();
    document.querySelectorAll('.bw-float[data-b]').forEach(clear);
  });

  /* --- Load-in ------------------------------------------------------------
   *
   * The same arrival as a blend swap — park off the nearest edge, then release
   * — triggered by the float scrolling into view instead of by a blend change.
   * Reusing flight() is the point: it is what makes a cut-out sitting half off
   * the left of the screen come in from the left, rather than from whichever
   * edge its centre happens to sit nearest.
   *
   * The flight is measured at intersection time, not up front. By the time a
   * float down in bw-how-to-use is reached the page has scrolled, and an edge
   * distance computed at load would point somewhere meaningless.
   */

  /* Enough to read as a sequence without the last piece feeling late. */
  var INTRO_STAGGER = 90;

  /* A float belonging to the inactive blend is visibility:hidden via its own
     section's CSS. Reading the computed style keeps that rule the one source of
     truth rather than re-deriving the blend match here — this script is
     deliberately section-agnostic. */
  function introEligible(el) {
    return getComputedStyle(el).visibility !== 'hidden';
  }

  function introduce(el, delay) {
    var rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      el.classList.remove('is-intro-pending');
      return;
    }

    var f = flight(rect);

    el.classList.remove('is-intro-pending');
    el.classList.add('is-swap-arm');
    el.style.transform = 'translate3d(' + f.x.toFixed(1) + 'px, ' + f.y.toFixed(1) + 'px, 0)';

    /* Commit the parked position as the transition's start value before the
       class that animates it lands. */
    void document.documentElement.offsetWidth;

    var inMs = ms('--bw-dur-swap-in', 260) + ms('--bw-delay-swap-in', 70);

    setTimeout(function () {
      el.classList.remove('is-swap-arm');
      el.classList.add('is-swap-in');
      el.style.transform = '';
      el._bwSwapTimer = setTimeout(function () { clear(el); }, inMs + 60);
    }, delay);
  }

  function armIntro() {
    var floats = [].slice.call(document.querySelectorAll('.bw-float'));

    if (!floats.length || reduce.matches || !('IntersectionObserver' in window)) {
      release();
      return;
    }

    floats.forEach(function (el) { el.classList.add('is-intro-pending'); });
    release();

    var io = new IntersectionObserver(function (entries) {
      /* Staggered per batch, so floats sharing a section arrive in sequence
         while one scrolled to on its own still comes in immediately. */
      var n = 0;
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);

        if (!introEligible(entry.target)) {
          entry.target.classList.remove('is-intro-pending');
          return;
        }

        introduce(entry.target, n * INTRO_STAGGER);
        n++;
      });
    }, { threshold: 0.01 });

    floats.forEach(function (el) { io.observe(el); });
  }

  /* Dead man's switch. The pre-paint hide is the one piece of this that fails
     destructively — if anything above throws, the floats would stay invisible
     for good. Pending floats are deliberately not cleared here: one far down
     the page is meant to sit hidden until it is scrolled to. */
  /* Lifts the pre-paint hide. Called on every path out of armIntro(), and
     again on a timer below in case none of them is reached. */
  function release() {
    document.documentElement.classList.remove('bw-float-intro');
  }

  setTimeout(release, 3000);

  /* rAF so first paint still has the floats hidden, and so rects are measured
     against a settled layout rather than mid-parse. */
  requestAnimationFrame(armIntro);
})();
